import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ExternalLink, Github, Calendar, Users, Star, Award } from 'lucide-react';
import { Project } from '@/types/portfolio';

interface PortfolioModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
}

const PortfolioModal: React.FC<PortfolioModalProps> = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!project) return null;

  const getMetricIcon = (key: string) => {
    switch (key.toLowerCase()) {
      case 'dau':
      case 'users':
      case 'traffic':
        return <Users className="w-4 h-4" />;
      case 'rating':
      case 'satisfaction':
        return <Star className="w-4 h-4" />;
      case 'award':
        return <Award className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="relative w-full max-w-6xl max-h-[90vh] bg-zinc-950 rounded-2xl overflow-hidden border border-zinc-800 pointer-events-auto shadow-2xl">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-6 right-6 z-10 p-2 rounded-lg bg-zinc-900/50 hover:bg-zinc-800 transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
              
              {/* Content */}
              <div className="overflow-y-auto max-h-[90vh]">
                <div className="grid md:grid-cols-2 min-h-[600px]">
                  {/* Left side - Image Gallery */}
                  <div className="relative bg-zinc-900 p-8 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10" />
                    <div className="relative z-10 text-center">
                      <h3 className="text-6xl font-bold text-zinc-800 mb-4">{project.category.toUpperCase()}</h3>
                      <p className="text-zinc-600">이미지 갤러리 추가 예정</p>
                    </div>
                  </div>
                  
                  {/* Right side - Project Details */}
                  <div className="p-8 md:p-12 bg-zinc-950">
                    {/* Header */}
                    <div className="mb-8">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="px-3 py-1 text-sm bg-zinc-800 text-zinc-400 rounded-full">
                          {project.status}
                        </span>
                        <span className="text-sm text-zinc-500">{project.period}</span>
                      </div>
                      
                      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        {project.title}
                      </h2>
                      
                      <p className="text-lg text-zinc-400 leading-relaxed">
                        {project.description}
                      </p>
                    </div>
                    
                    {/* Metrics */}
                    {project.metrics && Object.keys(project.metrics).length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-4">
                          프로젝트 성과
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(project.metrics).map(([key, value]) => (
                            <div key={key} className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
                              <div className="flex items-center gap-2 mb-2">
                                {getMetricIcon(key)}
                                <span className="text-xs text-zinc-500 capitalize">{key}</span>
                              </div>
                              <div className="text-xl font-semibold text-white">
                                {Array.isArray(value) ? value.join(', ') : value}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Features */}
                    {project.features && project.features.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-4">
                          주요 기능
                        </h3>
                        <ul className="space-y-3">
                          {project.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mt-2 flex-shrink-0" />
                              <span className="text-zinc-300">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Tech Stack */}
                    <div className="mb-8">
                      <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-4">
                        기술 스택
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {project.tags.map((tech) => (
                          <span
                            key={tech}
                            className="px-3 py-1.5 text-sm bg-zinc-800/50 text-zinc-300 rounded-lg border border-zinc-700/50"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    {project.link && (
                      <div className="flex gap-4">
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 text-black font-medium rounded-lg hover:bg-yellow-500 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          프로젝트 보기
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PortfolioModal;